/*
 * Geometry for the map draw tools. Framework-free so it can be shared by any map surface.
 *
 * Shapes kept while a boundary is on the map:
 *   { type: 'circle', center: LatLng, radius: <metres> }
 *   { type: 'rectangle', sw: LatLng, ne: LatLng }
 *   { type: 'free', path: [LatLng] }   (open path; closed when converted to points)
 *
 * Whatever the type, the page only ever receives the flat "lat,lng" point list (shapeToPoints),
 * which is the format the search/comps APIs and saved searches already use.
 */

export const CIRCLE_POINT_COUNT = 24;

const maps = () => window.google.maps;
const spherical = () => window.google.maps.geometry.spherical;
const toLatLng = (lat, lng) => new (maps().LatLng)(lat, lng);
const round6 = (value) => Math.round(value * 1e6) / 1e6;

/** Box spanned by two opposite corners. `square` keeps both sides equal in metres (circle). */
export const boxFromCorners = (anchor, cursor, square = false) => {
  let oppositeLat = cursor.lat();
  let oppositeLng = cursor.lng();

  if (square) {
    const northSouth = spherical().computeDistanceBetween(
      anchor,
      toLatLng(cursor.lat(), anchor.lng())
    );
    const eastWest = spherical().computeDistanceBetween(
      anchor,
      toLatLng(anchor.lat(), cursor.lng())
    );
    const side = Math.max(northSouth, eastWest);
    oppositeLat = spherical()
      .computeOffset(anchor, side, cursor.lat() >= anchor.lat() ? 0 : 180)
      .lat();
    oppositeLng = spherical()
      .computeOffset(anchor, side, cursor.lng() >= anchor.lng() ? 90 : 270)
      .lng();
  }

  return {
    sw: toLatLng(Math.min(anchor.lat(), oppositeLat), Math.min(anchor.lng(), oppositeLng)),
    ne: toLatLng(Math.max(anchor.lat(), oppositeLat), Math.max(anchor.lng(), oppositeLng)),
  };
};

/**
 * A drawn box must be at least MIN_RESIZE_SPAN_DEG (about 11 m) on both sides to become a shape.
 * A press without a drag, or a slightly shaky click, stays in the tool instead of creating a tiny
 * shape that runs an empty search and is too small for Edit Shape to resize.
 */
export const isUsableBox = (box) =>
  Boolean(box) &&
  box.ne.lat() - box.sw.lat() >= MIN_RESIZE_SPAN_DEG &&
  box.ne.lng() - box.sw.lng() >= MIN_RESIZE_SPAN_DEG;

/** Box corners, clockwise from the north-west: [NW, NE, SE, SW]. Opposite corner = (i + 2) % 4. */
export const boxCorners = ({ sw, ne }) => [
  toLatLng(ne.lat(), sw.lng()),
  toLatLng(ne.lat(), ne.lng()),
  toLatLng(sw.lat(), ne.lng()),
  toLatLng(sw.lat(), sw.lng()),
];

export const pathBox = (path) => {
  const lats = path.map((p) => p.lat());
  const lngs = path.map((p) => p.lng());
  return {
    sw: toLatLng(Math.min(...lats), Math.min(...lngs)),
    ne: toLatLng(Math.max(...lats), Math.max(...lngs)),
  };
};

export const circleFromBox = ({ sw, ne }) => {
  const center = toLatLng((sw.lat() + ne.lat()) / 2, (sw.lng() + ne.lng()) / 2);
  return {
    type: 'circle',
    center,
    radius: spherical().computeDistanceBetween(center, toLatLng(ne.lat(), center.lng())),
  };
};

/**
 * Circle drawn per the PRD: the first press is a point on the circle's edge and stays there, and
 * the cursor is the opposite point of the edge, so the circle grows from the first point towards
 * the cursor.
 */
export const circleFromEdgePoints = (anchor, cursor) => ({
  type: 'circle',
  center: spherical().interpolate(anchor, cursor, 0.5),
  radius: spherical().computeDistanceBetween(anchor, cursor) / 2,
});

/** Build a circle (square-constrained) or rectangle from a dragged box. */
export const shapeFromBox = (type, box) =>
  type === 'circle' ? circleFromBox(box) : { type: 'rectangle', sw: box.sw, ne: box.ne };

/** The box the edit handles sit on. */
export const shapeBox = (shape) => {
  if (shape.type === 'circle') {
    const { center, radius } = shape;
    return {
      sw: toLatLng(
        spherical().computeOffset(center, radius, 180).lat(),
        spherical().computeOffset(center, radius, 270).lng()
      ),
      ne: toLatLng(
        spherical().computeOffset(center, radius, 0).lat(),
        spherical().computeOffset(center, radius, 90).lng()
      ),
    };
  }
  if (shape.type === 'rectangle') return { sw: shape.sw, ne: shape.ne };
  return pathBox(shape.path);
};

// The smallest box side (about 11 m) a resize works with: a resize never shrinks a side below it,
// so a shape can't collapse to a point and get stuck there, and a shape with a side already below
// it (flat, collinear or just very thin) isn't resized at all.
const MIN_RESIZE_SPAN_DEG = 1e-4;

const boxSpans = (box) => ({
  latSpan: box.ne.lat() - box.sw.lat(),
  lngSpan: box.ne.lng() - box.sw.lng(),
});

/**
 * Box scaled proportionally (same factor on both sides, PRD: no non-proportional scaling) from
 * `fixedCorner` towards the dragged point. The factor is the larger of the two drag ratios so the
 * corner follows whichever direction the user pulls further. Both sides of `oldBox` must be at least
 * MIN_RESIZE_SPAN_DEG.
 */
const proportionalBox = (oldBox, fixedCorner, dragLatLng) => {
  const { latSpan, lngSpan } = boxSpans(oldBox);
  const dLat = dragLatLng.lat() - fixedCorner.lat();
  const dLng = dragLatLng.lng() - fixedCorner.lng();
  // Neither side may shrink below the minimum.
  const minScale = Math.max(MIN_RESIZE_SPAN_DEG / latSpan, MIN_RESIZE_SPAN_DEG / lngSpan);
  const scale = Math.max(Math.abs(dLat) / latSpan, Math.abs(dLng) / lngSpan, minScale);
  const opposite = toLatLng(
    fixedCorner.lat() + (dLat < 0 ? -1 : 1) * latSpan * scale,
    fixedCorner.lng() + (dLng < 0 ? -1 : 1) * lngSpan * scale
  );
  return boxFromCorners(fixedCorner, opposite);
};

/** Resize from `startShape` by dragging one corner while the opposite corner stays fixed. */
export const resizeShape = (startShape, fixedCorner, dragLatLng) => {
  // Circle: square box in metres, so it can never become an ellipse.
  if (startShape.type === 'circle')
    return circleFromBox(boxFromCorners(fixedCorner, dragLatLng, true));

  const old = shapeBox(startShape);
  const oldSpans = boxSpans(old);
  // A flat or very thin shape (e.g. collinear saved-search points) can't be scaled proportionally:
  // dividing by its tiny side would stretch it hundreds of km, or to the size of the globe.
  if (oldSpans.latSpan < MIN_RESIZE_SPAN_DEG || oldSpans.lngSpan < MIN_RESIZE_SPAN_DEG) return startShape;

  const box = proportionalBox(old, fixedCorner, dragLatLng);
  if (startShape.type === 'rectangle') return { type: 'rectangle', sw: box.sw, ne: box.ne };

  // Free shape: the whole shape scales (e.g. an "8" keeps its look) — every point is moved from
  // the old box into the new one with the same factor both ways.
  const newSpans = boxSpans(box);
  const latScale = newSpans.latSpan / oldSpans.latSpan;
  const lngScale = newSpans.lngSpan / oldSpans.lngSpan;

  return {
    type: 'free',
    path: startShape.path.map((p) =>
      toLatLng(
        box.sw.lat() + (p.lat() - old.sw.lat()) * latScale,
        box.sw.lng() + (p.lng() - old.sw.lng()) * lngScale
      )
    ),
  };
};

const closePath = (path) =>
  path.length > 2 &&
  (path[0].lat() !== path[path.length - 1].lat() || path[0].lng() !== path[path.length - 1].lng())
    ? [...path, path[0]]
    : path;

/** The flat "lat,lng" list handed to onDraw — same formats the draw tools produced before. */
export const shapeToPoints = (shape) => {
  if (shape.type === 'circle') {
    const points = [];
    for (let i = 0; i < CIRCLE_POINT_COUNT; i++) {
      const point = spherical().computeOffset(
        shape.center,
        shape.radius,
        (i * 360) / CIRCLE_POINT_COUNT
      );
      points.push(`${round6(point.lat())},${round6(point.lng())}`);
    }
    points.push(points[0]);
    return points;
  }

  const path =
    shape.type === 'rectangle'
      ? [...boxCorners(shape), boxCorners(shape)[0]]
      : closePath(shape.path);
  return path.map((c) => `${c.lat()},${c.lng()}`);
};

/** A shapeDefinition may arrive as a "/"-joined string, an array or an Immutable List. */
export const definitionKey = (definition) => {
  if (!definition) return '';
  if (typeof definition === 'string') return definition;
  return definition.join ? definition.join('/') : String(definition);
};

/** Smallest difference between two bearings in degrees (0-180). */
const bearingDiff = (a, b) => Math.abs((((a - b) % 360) + 540) % 360 - 180);

/**
 * A circle comes back as exactly the points shapeToPoints made for it: CIRCLE_POINT_COUNT points,
 * point i at bearing i × 15° from the centre (the first due north, clockwise), all at the same
 * distance. Recognised so it's drawn and edited as a real circle again, not a 24-sided polygon. Any
 * other 24 points, even ones lying on a circle but spaced unevenly, stay a free shape, so the map
 * never shows a boundary the search isn't using. Allows 1% (or 1 m) on the radius and 1° on each
 * bearing, for the points being rounded to 6 decimals.
 */
const circleFromPoints = (path) => {
  if (path.length !== CIRCLE_POINT_COUNT) return null;

  const center = spherical().interpolate(path[0], path[CIRCLE_POINT_COUNT / 2], 0.5);
  const distances = path.map((p) => spherical().computeDistanceBetween(center, p));
  const radius = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  const tolerance = Math.max(1, radius * 0.01);
  const step = 360 / CIRCLE_POINT_COUNT;

  const isOurCircle =
    radius > 0 &&
    distances.every((d) => Math.abs(d - radius) <= tolerance) &&
    path.every((p, i) => bearingDiff(spherical().computeHeading(center, p), i * step) <= 1);

  return isOurCircle ? { type: 'circle', center, radius } : null;
};

/**
 * Shapes that come back as points (the page passing our own points back after a re-mount, or a saved
 * search) are rebuilt from them: a circle we drew becomes a circle again; anything else edits as a
 * free shape.
 */
export const pointsToShape = (definition) => {
  const path = definitionKey(definition)
    .split('/')
    .filter(Boolean)
    .map((p) =>
      toLatLng(Number(p.substr(0, p.indexOf(','))), Number(p.substr(p.indexOf(',') + 1)))
    );

  if (path.length > 1) {
    const first = path[0];
    const last = path[path.length - 1];
    if (first.lat() === last.lat() && first.lng() === last.lng()) path.pop();
  }

  if (path.length < 3) return null;
  return circleFromPoints(path) || { type: 'free', path };
};

/** Read the geometry back from a dragged/edited Google overlay. */
export const shapeFromOverlay = (type, overlay) => {
  if (type === 'circle')
    return { type: 'circle', center: overlay.getCenter(), radius: overlay.getRadius() };

  const path = overlay
    .getPath()
    .getArray()
    .map((p) => toLatLng(p.lat(), p.lng()));
  if (type === 'rectangle') {
    const box = pathBox(path);
    return { type: 'rectangle', sw: box.sw, ne: box.ne };
  }
  return { type: 'free', path };
};
